import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUturnLeftIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ItineraryChangeEntry, ItineraryChanges } from '@/utils/itineraryChanges.ts';
import { capitalizeFirstLetter } from '@/utils/capitalizeFirstLetter.ts';
import { missingTranslationToReadable } from '@/utils/missingTranslationToReadable.ts';
import { cn } from '@/utils/classNames.ts';
import { pillClassName } from '@/components/shared/tripControlClassNames.ts';

type TripChangeSummaryProps = {
  changes: ItineraryChanges;
  ask?: string | null;
  onJumpToDay: (day: number) => void;
  onDismiss: () => void;
  onUndo?: () => void;
  canUndo?: boolean;
  isUndoing?: boolean;
  className?: string;
};

const TripChangeSummary: FC<TripChangeSummaryProps> = ({
  changes,
  ask,
  onJumpToDay,
  onDismiss,
  onUndo,
  canUndo = false,
  isUndoing = false,
  className,
}) => {
  const { t } = useTranslation();

  const translatePartOfTheDay = (partOfTheDay: string) =>
    t(
      `enum_translations.PartOfTheDay.${partOfTheDay}`,
      capitalizeFirstLetter(missingTranslationToReadable(partOfTheDay))
    );

  function getLocation(entry: ItineraryChangeEntry): string {
    if (!('day' in entry)) return t('tripPage.changes.accommodation_label');

    const day = t('tripPage.changes.day_label', { number: entry.day });

    return 'partOfTheDay' in entry ? `${day} · ${translatePartOfTheDay(entry.partOfTheDay)}` : day;
  }

  function getText(entry: ItineraryChangeEntry): string {
    switch (entry.kind) {
      case 'day_added':
        return t('tripPage.changes.entries.day_added', { title: entry.title });
      case 'day_removed':
        return t('tripPage.changes.entries.day_removed', { title: entry.title });
      case 'day_title':
        return t('tripPage.changes.entries.day_title', { title: entry.title, previousTitle: entry.previousTitle });
      case 'day_dining':
        return t('tripPage.changes.entries.day_dining');
      case 'day_local_secret':
        return t('tripPage.changes.entries.day_local_secret');
      case 'activity_added':
        return t('tripPage.changes.entries.activity_added', { title: entry.title });
      case 'activity_replaced':
        return t('tripPage.changes.entries.activity_replaced', {
          title: entry.title,
          previousTitle: entry.previousTitle,
        });
      case 'activity_refined':
        return t('tripPage.changes.entries.activity_refined', { title: entry.title });
      case 'activity_moved':
        return t('tripPage.changes.entries.activity_moved', {
          title: entry.title,
          previousPartOfTheDay: translatePartOfTheDay(entry.previousPartOfTheDay),
        });
      case 'activity_removed':
        return t('tripPage.changes.entries.activity_removed', { title: entry.title });
      case 'accommodation_replaced':
        return t('tripPage.changes.entries.accommodation_replaced', {
          place: entry.place,
          previousPlace: entry.previousPlace,
        });
      case 'accommodation_refined':
        return t('tripPage.changes.entries.accommodation_refined', { place: entry.place });
    }
  }

  const showUndo = !!onUndo && (canUndo || isUndoing);

  return (
    <section
      aria-labelledby="trip-change-summary-title"
      className={cn('rounded-[14px] border-2 border-primary bg-primary/10 px-5 py-5 md:px-7 md:py-6', className)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <SparklesIcon aria-hidden="true" className="mt-1 h-6 w-6 shrink-0 text-primary" />
          <div>
            <h2
              id="trip-change-summary-title"
              className="font-feature text-2xl font-semibold text-[#2F2D2A] md:text-3xl"
            >
              {t('tripPage.changes.title')}
            </h2>
            <p className="mt-1 font-sans text-sm text-text-secondary">
              {ask
                ? t('tripPage.changes.you_asked', { ask })
                : t('tripPage.changes.count', { count: changes.entries.length })}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {showUndo ? (
            <UndoButton onUndo={onUndo} canUndo={canUndo} isUndoing={isUndoing} className="hidden md:inline-flex" />
          ) : null}
          <button
            type="button"
            onClick={onDismiss}
            aria-label={t('tripPage.changes.dismiss')}
            className="shrink-0 rounded-full p-1 text-[#3F3D3A] transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <XMarkIcon aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
      </div>

      <ul className="mt-4 flex flex-col gap-2">
        {changes.entries.map((entry, index) => {
          const row = {
            key: `${entry.kind}-${index}`,
            day: 'day' in entry ? entry.day : null,
            location: getLocation(entry),
            text: getText(entry),
          };

          return (
            <li key={row.key}>
              {row.day !== null ? (
                <button
                  type="button"
                  onClick={() => onJumpToDay(row.day!)}
                  className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-1 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <ChangeLocation label={row.location} />
                  <span className="text-sm text-[#3F3D3A] md:text-base">{row.text}</span>
                </button>
              ) : (
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-2 py-1.5">
                  <ChangeLocation label={row.location} />
                  <span className="text-sm text-[#3F3D3A] md:text-base">{row.text}</span>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {showUndo ? (
        <div className="mt-4 md:hidden">
          <UndoButton onUndo={onUndo} canUndo={canUndo} isUndoing={isUndoing} />
        </div>
      ) : null}
    </section>
  );
};

type UndoButtonProps = { onUndo?: () => void; canUndo: boolean; isUndoing: boolean; className?: string };

const UndoButton: FC<UndoButtonProps> = ({ onUndo, canUndo, isUndoing, className }) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onUndo}
      disabled={!canUndo || isUndoing}
      aria-busy={isUndoing}
      className={cn(pillClassName, 'gap-1.5 bg-base-100/60 font-semibold', className)}
    >
      <ArrowUturnLeftIcon aria-hidden="true" className="h-4 w-4 text-primary" />
      {isUndoing ? t('tripPage.changes.undoing') : t('tripPage.changes.undo')}
    </button>
  );
};

const ChangeLocation: FC<{ label: string }> = ({ label }) => (
  <span className="shrink-0 font-sans text-xs font-semibold uppercase tracking-wide text-primary">{label}</span>
);

export default TripChangeSummary;
